/**
 * Error reporter — posts application errors to the error_queue table via the
 * analytics debug endpoint. Fire-and-forget: failures are silently swallowed
 * so the reporter never breaks the page it's running on.
 *
 * Only "bug" categories are recorded:
 *   query_error, query_transform_error, database_error, internal_error,
 *   runtime_error, frontend_error, frontend_promise
 *
 * Expected / user-caused codes (not_found, invalid_request, unauthorized,
 * auth_required) are intentionally ignored.
 */

const BUG_CATEGORIES = new Set([
  'query_error',
  'query_transform_error',
  'database_error',
  'internal_error',
  'runtime_error',
  'frontend_error',
  'frontend_promise',
]);

const API_URL = import.meta.env.VITE_API_URL ?? '';
const REPORT_URL = `${API_URL}/api/v1/analytics/debug/errors`;

function post(category, message, context = {}) {
  // Best-effort fire and forget — never await, never throw.
  try {
    fetch(REPORT_URL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, message, context }),
    }).catch(() => {});
  } catch (_) {
    // ignore
  }
}

/**
 * Called by the API client when a response has a structured error body.
 * Reports only bug-category errors; skips expected user-facing errors.
 */
export function reportApiError(errorCode, message, context = {}) {
  if (!BUG_CATEGORIES.has(errorCode)) return;
  post(errorCode, message, { ...context, source: 'api_response' });
}

/**
 * Install global handlers for uncaught JS errors and unhandled promise
 * rejections. Call once from main.jsx.
 */
export function initGlobalErrorReporting() {
  window.addEventListener('error', (event) => {
    if (!event.error) return; // ignore events without an Error object (e.g. resource load failures)
    post('frontend_error', event.message, {
      source: 'window.onerror',
      url: window.location.href,
      file: `${event.filename}:${event.lineno}:${event.colno}`,
      stack: event.error?.stack?.slice(0, 500),
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const msg = event.reason instanceof Error
      ? event.reason.message
      : String(event.reason);
    post('frontend_promise', msg, {
      source: 'unhandledrejection',
      url: window.location.href,
      stack: event.reason instanceof Error ? event.reason.stack?.slice(0, 500) : undefined,
    });
  });
}
